import type { FeedbackPayload, Message } from '../state';

/**
 * Whole seconds since a start time, rounded up.
 * @param startedAtMs - Start time in epoch milliseconds.
 * @returns The seconds, never below zero.
 */
export const getDurationSeconds = (startedAtMs: number): number =>
  Math.max(0, Math.ceil((Date.now() - startedAtMs) / 1000));

/**
 * Whether an answer carries a chart.
 * @param message - The answer.
 * @returns True when it holds a visualization or its image links.
 */
export const hasVisualization = (message: Message): boolean =>
  message.visualization !== undefined ||
  (message.visualizationUrls?.length ?? 0) > 0;

/**
 * Keys each stored rating by what it says, so only a changed rating
 * reports again.
 * @param submitted - The stored ratings, by message id.
 * @returns Their signatures, by message id.
 */
export const readFeedbackSignatures = (
  submitted: Readonly<Record<string, FeedbackPayload>>
): ReadonlyMap<string, string> =>
  new Map(
    Object.entries(submitted).map(([messageId, { rating, comment }]) => [
      messageId,
      JSON.stringify([rating, comment ?? null]),
    ])
  );

/**
 * Event fields for a trace id, left out when there is none.
 * @param traceId - The backend trace id.
 * @returns `{ traceId }`, or nothing.
 */
export const withTraceId = (traceId: string | undefined) =>
  traceId !== undefined && traceId !== '' ? { traceId } : {};
